import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import FolderDeleteIcon from '@mui/icons-material/FolderDelete';
import { Text, TextField, GridLayout, GridCell } from '@qwickapps/react-framework';
import { useAuth } from '@qwickapps/auth-client';
import { useMiddlewareClient, type SystemSettings, type SubscriptionInfo } from '@qwickforge/react-client';
import UpgradeNotice from '../components/UpgradeNotice';

interface Project {
  id: string;
  name: string;
  description?: string;
  slug: string;
  currentDir?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export default function ProjectsPage() {
  const { session } = useAuth();
  const client = useMiddlewareClient();
  const [projects, setProjects] = useState<Project[]>([]);
  const [trashedProjects, setTrashedProjects] = useState<Project[]>([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [systemSettings, setSystemSettings] = useState<SystemSettings>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'active' | 'trash'>('active');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Form states
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (session?.accessToken) {
      loadProjects();
      loadSubscriptionInfo();
    }
    // Load system settings (no auth required)
    loadSystemSettings();
  }, [session]);

  const loadSystemSettings = async () => {
    if (!client) return;
    try {
      const settings = await client.systemSettings.getAll();
      setSystemSettings(settings);
    } catch (err) {
      console.error('Failed to load system settings:', err);
      // Non-critical, continue without settings
    }
  };

  const loadProjects = async () => {
    if (!client) return;

    try {
      setLoading(true);
      setError('');
      const data = await client.projects.list();
      const allProjects = Array.isArray(data) ? data : [];

      // Separate active and trashed projects
      setProjects(allProjects.filter(p => !p.deletedAt));
      setTrashedProjects(allProjects.filter(p => p.deletedAt));
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionInfo = async () => {
    if (!client) return;
    try {
      const info = await client.subscription.getInfo();
      setSubscriptionInfo(info);
    } catch (err) {
      console.error('Failed to load subscription info:', err);
      // Set safe defaults on error
      setSubscriptionInfo({
        plan: 'free',
        status: 'active',
        currentProjects: projects.length,
        projectLimit: 1,
        canCreateMore: false,
        subscription: null,
      });
    }
  };

  const handleCreateProject = async () => {
    if (!client || !projectName.trim()) return;

    try {
      setSubmitting(true);
      setError('');

      const newProject = await client.projects.create({
        name: projectName.trim(),
        description: projectDescription.trim() || undefined,
      });

      setProjects([...projects, newProject]);
      setCreateDialogOpen(false);
      setProjectName('');
      setProjectDescription('');

      // Refresh subscription info
      loadSubscriptionInfo();
    } catch (err: any) {
      console.error('Failed to create project:', err);
      const message = err.response?.data?.error?.message || err.message || 'Failed to create project';

      // Check if it's a limit error
      if (message.includes('limit reached') || message.includes('Project limit')) {
        setError(`Project limit reached. Free plan allows 1 project. Upgrade to create more.`);
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditProject = async () => {
    if (!client || !selectedProject || !projectName.trim()) return;

    try {
      setSubmitting(true);
      setError('');

      const updated = await client.projects.update(selectedProject.id, {
        name: projectName.trim(),
        description: projectDescription.trim() || undefined,
      });

      setProjects(projects.map(p => p.id === selectedProject.id ? updated : p));
      setEditDialogOpen(false);
      setSelectedProject(null);
      setProjectName('');
      setProjectDescription('');
    } catch (err) {
      console.error('Failed to update project:', err);
      setError(err instanceof Error ? err.message : 'Failed to update project');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!client || !selectedProject) return;

    try {
      setSubmitting(true);
      setError('');

      // Soft delete (move to trash)
      await client.projects.softDelete(selectedProject.id);

      // Move from active to trash
      setProjects(projects.filter(p => p.id !== selectedProject.id));
      setTrashedProjects([...trashedProjects, { ...selectedProject, deletedAt: new Date().toISOString() }]);
      setDeleteDialogOpen(false);
      setSelectedProject(null);

      // Refresh subscription info
      loadSubscriptionInfo();
    } catch (err: any) {
      console.error('Failed to delete project:', err);
      const message = err.response?.data?.error?.message || err.message || 'Failed to delete project';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestoreProject = async () => {
    if (!client || !selectedProject) return;

    try {
      setSubmitting(true);
      setError('');

      // Check if user can create more projects before restoring
      if (subscriptionInfo && !subscriptionInfo.canCreateMore) {
        setError(`Cannot restore project. You've reached your limit (${subscriptionInfo.projectLimit} projects on ${subscriptionInfo.plan.toUpperCase()} plan). Delete an active project or upgrade to restore.`);
        return;
      }

      await client.projects.restore(selectedProject.id);

      // Move from trash to active
      setTrashedProjects(trashedProjects.filter(p => p.id !== selectedProject.id));
      setProjects([...projects, { ...selectedProject, deletedAt: null }]);
      setRestoreDialogOpen(false);
      setSelectedProject(null);

      // Refresh subscription info
      loadSubscriptionInfo();
    } catch (err: any) {
      console.error('Failed to restore project:', err);
      const message = err.response?.data?.error?.message || err.message || 'Failed to restore project';

      if (message.includes('limit') || message.includes('quota')) {
        setError(`Cannot restore. ${message}`);
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!client || !selectedProject) return;

    if (!deleteReason.trim()) {
      setError('Please provide a reason for permanent deletion');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      await client.projects.permanentDelete(selectedProject.id, deleteReason.trim());

      // Remove from trash
      setTrashedProjects(trashedProjects.filter(p => p.id !== selectedProject.id));
      setPermanentDeleteDialogOpen(false);
      setSelectedProject(null);
      setDeleteReason('');
    } catch (err: any) {
      console.error('Failed to permanently delete project:', err);
      const message = err.response?.data?.error?.message || err.message || 'Failed to permanently delete project';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const openCreateDialog = () => {
    setProjectName('');
    setProjectDescription('');
    setError('');
    setCreateDialogOpen(true);
  };

  const openEditDialog = (project: Project) => {
    setSelectedProject(project);
    setProjectName(project.name);
    setProjectDescription(project.description || '');
    setError('');
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (project: Project) => {
    setSelectedProject(project);
    setError('');
    setDeleteDialogOpen(true);
  };

  const openRestoreDialog = (project: Project) => {
    setSelectedProject(project);
    setError('');
    setRestoreDialogOpen(true);
  };

  const openPermanentDeleteDialog = (project: Project) => {
    setSelectedProject(project);
    setDeleteReason('');
    setError('');
    setPermanentDeleteDialogOpen(true);
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'free': return 'default';
      case 'pro': return 'primary';
      case 'enterprise': return 'secondary';
      default: return 'default';
    }
  };

  const getUsageColor = (current: number, limit: number): 'info' | 'warning' | 'error' => {
    const percentage = (current / limit) * 100;
    if (percentage >= 100) return 'error';
    if (percentage >= 50) return 'warning';
    return 'info';
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 8 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Text variant="h4" customColor="var(--theme-text-primary)" gutterBottom content="Projects" />
            <Text variant="body2" customColor="var(--theme-text-primary)" content="Organize and manage your AI projects" />
            {subscriptionInfo && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                <Chip
                  label={`${subscriptionInfo.plan.toUpperCase()} Plan`}
                  size="small"
                  sx={{
                    backgroundColor: subscriptionInfo.plan === 'pro' ? 'var(--theme-primary)' : subscriptionInfo.plan === 'enterprise' ? 'var(--theme-secondary)' : 'var(--theme-surface)',
                    color: subscriptionInfo.plan === 'pro' ? 'var(--theme-on-primary)' : subscriptionInfo.plan === 'enterprise' ? 'var(--theme-on-secondary)' : 'var(--theme-text-secondary)'
                  }}
                />
                <Chip
                  label={`${subscriptionInfo.currentProjects} / ${subscriptionInfo.projectLimit} projects`}
                  size="small"
                  sx={{
                    backgroundColor: getUsageColor(subscriptionInfo.currentProjects, subscriptionInfo.projectLimit) === 'error' ? 'var(--theme-error)' : getUsageColor(subscriptionInfo.currentProjects, subscriptionInfo.projectLimit) === 'warning' ? 'var(--theme-warning)' : 'var(--theme-surface)',
                    color: getUsageColor(subscriptionInfo.currentProjects, subscriptionInfo.projectLimit) === 'error' ? 'var(--theme-on-error)' : getUsageColor(subscriptionInfo.currentProjects, subscriptionInfo.projectLimit) === 'warning' ? 'var(--theme-text-primary)' : 'var(--theme-text-secondary)'
                  }}
                />
              </Box>
            )}
          </Box>
          <Tooltip
            title={subscriptionInfo?.canCreateMore ? 'Create new project' : 'Project limit reached. Upgrade to create more.'}
            arrow
          >
            <span>
              <Button
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
                disabled={!subscriptionInfo?.canCreateMore}
                variant='outlined'
              >
                New Project
              </Button>
            </span>
          </Tooltip>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Upgrade Notice */}
        {subscriptionInfo && !subscriptionInfo.canCreateMore && (
          <UpgradeNotice
            message={`You've reached your project limit (${subscriptionInfo.projectLimit} project${subscriptionInfo.projectLimit !== 1 ? 's' : ''} allowed on ${subscriptionInfo.plan.toUpperCase()} plan).`}
          />
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            mb: 3,
            '& .MuiTab-root': {
              color: 'var(--theme-text-secondary)'
            },
            '& .Mui-selected': {
              color: 'var(--theme-primary)'
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'var(--theme-primary)'
            }
          }}
        >
          <Tab label={`Active (${projects.length})`} value="active" />
          <Tab label={`Trash (${trashedProjects.length})`} value="trash" />
        </Tabs>

        {/* Active Projects View */}
        {activeTab === 'active' && (
          projects.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <FolderIcon sx={{ fontSize: 64, color: 'var(--theme-text-secondary)', mb: 2 }} />
              <Text variant="h6" gutterBottom content="No projects yet" />
              <Text
                variant="body2"
                customColor="var(--theme-text-secondary)"
                content="Create your first project to get started"
              />
            </Box>
          ) : (
            <GridLayout spacing={3}>
              {projects.map((project) => (
                <GridCell xs={12} md={6} lg={4} key={project.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        boxShadow: 3,
                      }
                    }}
                  >
                    <CardContent sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <FolderIcon sx={{ color: 'var(--theme-primary)', mt: 0.5 }} />
                        <Text variant="h6" content={project.name} />
                      </Box>
                      {project.description && (
                        <Text
                          variant="body2"
                          customColor="var(--theme-secondary-dark)"
                          content={project.description}
                          sx={{ mb: 2 }}
                        />
                      )}
                      {project.currentDir && (
                        <Text
                          variant="caption"
                          customColor="var(--theme-primary-dark)"
                          content={`Current: ${project.currentDir}`}
                          sx={{ display: 'block', fontFamily: 'monospace', mt: 1 }}
                        />
                      )}
                      <Text
                        variant="caption"
                        customColor="var(--theme-text)"
                        content={`Updated: ${new Date(project.updatedAt).toLocaleDateString()}`}
                        sx={{ display: 'block', mt: 1 }}
                      />
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                      <Tooltip title="Edit project">
                        <IconButton size="small" onClick={() => openEditDialog(project)}>
                          <EditIcon fontSize="small" sx={{ color: 'var(--theme-primary)' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={projects.length === 1 ? 'Cannot delete last project' : 'Delete project'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => openDeleteDialog(project)}
                            disabled={projects.length === 1}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" sx={{ color: projects.length === 1 ? 'var(--theme-text-secondary)' : 'var(--theme-error)' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </GridCell>
              ))}
            </GridLayout>
          )
        )}

        {/* Trash View */}
        {activeTab === 'trash' && (
          trashedProjects.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <FolderDeleteIcon sx={{ fontSize: 64, color: 'var(--theme-text-secondary)', mb: 2 }} />
              <Text variant="h6" gutterBottom content="Trash is empty" />
              <Text
                variant="body2"
                customColor="var(--theme-text-secondary)"
                content="Deleted projects will appear here"
              />
            </Box>
          ) : (
            <GridLayout spacing={3}>
              {trashedProjects.map((project) => (
                <GridCell xs={12} md={6} lg={4} key={project.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      opacity: 0.7,
                      '&:hover': {
                        boxShadow: 3,
                        opacity: 1,
                      }
                    }}
                  >
                    <CardContent sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                        <FolderDeleteIcon sx={{ color: 'var(--theme-text-secondary)', mt: 0.5 }} />
                        <Text variant="h6" content={project.name} />
                      </Box>
                      {project.description && (
                        <Text
                          variant="body2"
                          customColor="var(--theme-text-secondary)"
                          content={project.description}
                          sx={{ mb: 2 }}
                        />
                      )}
                      <Text
                        variant="caption"
                        customColor="var(--theme-text-secondary)"
                        content={`Deleted: ${new Date(project.deletedAt!).toLocaleDateString()}`}
                        sx={{ display: 'block', mt: 1 }}
                      />
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                      <Tooltip title="Restore project">
                        <IconButton size="small" onClick={() => openRestoreDialog(project)}>
                          <RestoreIcon fontSize="small" sx={{ color: 'var(--theme-primary)' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete permanently">
                        <IconButton size="small" onClick={() => openPermanentDeleteDialog(project)}>
                          <DeleteForeverIcon fontSize="small" sx={{ color: 'var(--theme-error)' }} />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </GridCell>
              ))}
            </GridLayout>
          )
        )}

        {/* Create Project Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Project</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Project Name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="My Awesome Project"
                required
                autoFocus
              />
              <TextField
                label="Description"
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                placeholder="A brief description of your project"
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateProject}
              disabled={!projectName.trim() || submitting}
            >
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Project Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Project</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Project Name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                required
                autoFocus
              />
              <TextField
                label="Description"
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                multiline
                rows={3}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleEditProject}
              disabled={!projectName.trim() || submitting}
            >
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Project Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Project</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This project will be moved to trash. You can restore it later or delete it permanently.
            </Alert>
            <Text
              variant="body1"
              content={`Move "${selectedProject?.name}" to trash?`}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDeleteProject}
              disabled={submitting}
              sx={{
                bgcolor: 'var(--theme-error)',
                '&:hover': { bgcolor: 'var(--theme-error)' }
              }}
            >
              {submitting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Restore Project Dialog */}
        <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Restore Project</DialogTitle>
          <DialogContent>
            <Text
              variant="body1"
              content={`Restore "${selectedProject?.name}"? It will be moved back to active projects.`}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRestoreDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRestoreProject}
              disabled={submitting}
              color="primary"
            >
              {submitting ? 'Restoring...' : 'Restore'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Permanent Delete Dialog */}
        <Dialog open={permanentDeleteDialogOpen} onClose={() => setPermanentDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Permanent Delete</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              This action CANNOT be undone. The project and all its data will be permanently deleted.
            </Alert>
            <Text
              variant="body1"
              content={`Permanently delete "${selectedProject?.name}"?`}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Reason for deletion (required)"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="e.g., Project no longer needed"
              multiline
              rows={3}
              required
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPermanentDeleteDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePermanentDelete}
              disabled={!deleteReason.trim() || submitting}
              sx={{
                bgcolor: 'var(--theme-error)',
                '&:hover': { bgcolor: 'var(--theme-error)' }
              }}
            >
              {submitting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
